import { useEffect, useRef, useState, useCallback } from "react";
import styled from "@emotion/styled";
import moment from "moment";

import WidgetBox, {
    CustomSpinner,
} from "components/dashboard/widgets/WidgetBox";
import { WidgetProps } from "types";
import {
    fetchGatewayOnlineStatus,
    fetchHeartbeatSumData,
} from "service/proqueryService";
import { DEFAULT_GATEWAY_NAME } from "constant/gateway";
import UptimeBars from "./components/UptimeBars";
import useGatewayStatus from "hooks/useGatewayStatus";
import WidgetContent from "components/dashboard/widgets/WidgetContent";

const StyledWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 15px;
    width: 100%;
    height: 100%;
    margin-top: 29px;
`;

type ChartPanel = {
    attributes: Array<{
        gateway_id: string;
    }>;
    name: string;
};

type MultipleGWStatusCardProps = Omit<WidgetProps, "panel"> & {
    panel: ChartPanel;
};

const MultiGatewayUptime = ({
    panel,
    allRegistry,
}: MultipleGWStatusCardProps) => {
    const daysOfData = 90;
    const refreshRate = 30; // 60 seconds is the interval of gateway heartbeat report, but it's same as the timeout of Websocket, so we set fetch interval to 30 seconds to ensure the connection is alive.
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [chartData, setChartData] = useState<{
        [gatewayId: string]: {
            groupByDay: Record<number, number>;
            totalMinutes: number;
        };
    } | null>(null);

    const gwStatus = useGatewayStatus(
        panel.attributes?.map((attr) => attr.gateway_id) || [],
        refreshRate
    );

    const fetchUptimeData = useCallback(async () => {
        const gatewayIds =
            panel.attributes?.map((attr) => attr.gateway_id) || [];

        const endTime = moment().endOf("day");
        const startTime = moment(endTime)
            .subtract(daysOfData-1, "days")
            .startOf("day");

        const data = await fetchHeartbeatSumData({
            gatewayIds,
            startTime: startTime.unix(),
            endTime: endTime.unix(),
        });

        return data;
    }, [panel.attributes]);

    const fetchOnlineStatus = useCallback(async () => {
        const gatewayIds =
            panel.attributes?.map((attr) => attr.gateway_id) || [];
        const gatewayStatus = await fetchGatewayOnlineStatus(gatewayIds);
        return gatewayStatus;
    }, [panel.attributes]);

    useEffect(() => {
        fetchUptimeData()
            .then((res) => {
                setChartData(res);
            })
            .finally(() => {
                setIsLoaded(true);
            });
    }, [fetchUptimeData, fetchOnlineStatus]);

    const gateways = panel.attributes?.map((attr, i) => {
        const gatewayId = attr.gateway_id;
        const gatewayDetail = (allRegistry ?? []).find(
            (gw: any) => gw.gateway_id === gatewayId
        );
        const isOnline = !!gwStatus?.[gatewayId]?.isOnline;
        const groupedHeartbeats = chartData?.[gatewayId];

        const result = {
            name: DEFAULT_GATEWAY_NAME,
            gatewayId,
            isOnline,
            groupedHeartbeats,
        };

        if (!gatewayDetail) {
            return result;
        }

        result.name = gatewayDetail.name;
        return result;
    });

    return (
        <WidgetBox ref={containerRef} className="widget-box w-100">
            <WidgetContent title={panel.name}>
                <StyledWrapper>
                    {isLoaded && (
                        <>
                            {gateways.map((gateway) => (
                                <UptimeBars
                                    key={gateway.gatewayId}
                                    onlineTimes={gateway.groupedHeartbeats ?? null}
                                    isOnline={gateway.isOnline}
                                    title={gateway.name ?? ""}
                                    subTitle={gateway.gatewayId ?? ""}
                                    barNum={daysOfData}
                                />
                            ))}
                        </>
                    )}

                    {!isLoaded && (
                        <div className="d-flex justify-content-center align-items-center mt-n5 flex-fill">
                            <CustomSpinner animation="border" />
                        </div>
                    )}
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default MultiGatewayUptime;
